import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import DashboardLayout from './DashboardLayout';
import styles from './FounderDashboard.module.css';
import { 
  FiTrendingUp, FiTarget, FiAward, FiUsers, 
  FiDollarSign, FiGlobe, FiCalendar, FiEye,
  FiArrowUp, FiArrowDown, FiCheckCircle, FiAlertCircle,
  FiBarChart2, FiPieChart, FiFlag, FiStar
} from 'react-icons/fi';

const FounderDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    companyValuation: 1500000000,
    marketShare: 24.5,
    employeeCount: 0,
    globalPresence: 12,
    revenueGrowth: 28.5,
    customerSatisfaction: 94.2
  });
  const [strategicInitiatives, setStrategicInitiatives] = useState([]);
  const [boardMetrics, setBoardMetrics] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    fetchUser();
    fetchDashboardData();
  }, []);

  const fetchUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      setUser(profile);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const { count: employeeCount } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });

      const { data: initiatives } = await supabase
        .from('strategic_initiatives')
        .select('*')
        .order('created_at', { ascending: false });

      const { data: activities } = await supabase
        .from('user_activity')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      setStats(prev => ({ ...prev, employeeCount: employeeCount || 0 }));
      setStrategicInitiatives(initiatives || []);
      setBoardMetrics([
        { metric: 'Revenue', value: '₹58.2M', change: '+28%', target: '₹50M' },
        { metric: 'Net Profit', value: '₹12.4M', change: '+32%', target: '₹10M' },
        { metric: 'EBITDA', value: '₹18.6M', change: '+25%', target: '₹15M' },
        { metric: 'Market Cap', value: '₹2.4B', change: '+45%', target: '₹2B' }
      ]);
      setRecentActivities(activities || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="Founder & Chairman">
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="Founder & Chairman">
      <div className={styles.founderDashboard}>
        <div className={styles.welcomeSection}>
          <div>
            <h1>Chairman's Dashboard</h1>
            <p>Strategic Overview & Corporate Governance</p>
          </div>
          <div className={styles.dateDisplay}>
            <FiCalendar />
            <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>

        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <div className={styles.kpiIcon} style={{ background: '#1a73e8' }}>
              <FiDollarSign />
            </div>
            <div className={styles.kpiInfo}>
              <h3>Company Valuation</h3>
              <p className={styles.kpiValue}>${(stats.companyValuation / 1000000).toFixed(0)}M</p>
              <span className={styles.kpiChange}>+45% YoY</span>
            </div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiIcon} style={{ background: '#0f9d58' }}>
              <FiGlobe />
            </div>
            <div className={styles.kpiInfo}>
              <h3>Market Share</h3>
              <p className={styles.kpiValue}>{stats.marketShare}%</p>
              <span className={styles.kpiChange}>+5.2% vs last year</span>
            </div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiIcon} style={{ background: '#e37400' }}>
              <FiUsers />
            </div>
            <div className={styles.kpiInfo}>
              <h3>Total Employees</h3>
              <p className={styles.kpiValue}>{stats.employeeCount}</p>
              <span className={styles.kpiChange}>+156 this year</span>
            </div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiIcon} style={{ background: '#d93025' }}>
              <FiTrendingUp />
            </div>
            <div className={styles.kpiInfo}>
              <h3>Revenue Growth</h3>
              <p className={styles.kpiValue}>+{stats.revenueGrowth}%</p>
              <span className={styles.kpiChange}>Year over Year</span>
            </div>
          </div>
        </div>

        <div className={styles.sectionCard}>
          <h2><FiFlag /> Strategic Initiatives</h2>
          <div className={styles.initiativesGrid}>
            {strategicInitiatives.map((initiative, index) => (
              <div key={index} className={styles.initiativeCard}>
                <div className={styles.initiativeHeader}>
                  <h3>{initiative.title}</h3>
                  <span className={`${styles.statusBadge} ${styles[initiative.status]}`}>
                    {initiative.status}
                  </span>
                </div>
                <p>{initiative.description}</p>
                <div className={styles.progressBar}>
                  <div className={styles.progress} style={{ width: `${initiative.progress}%` }}></div>
                </div>
                <div className={styles.progressInfo}>
                  <span>{initiative.progress}% Complete</span>
                  <span>Target: {new Date(initiative.target_date).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.sectionCard}>
          <h2><FiBarChart2 /> Board Performance Metrics</h2>
          <div className={styles.metricsTable}>
            <table>
              <thead>
                <tr><th>Metric</th><th>Current Value</th><th>Change</th><th>Target</th><th>Status</th></tr>
              </thead>
              <tbody>
                {boardMetrics.map((metric, index) => (
                  <tr key={index}>
                    <td>{metric.metric}</td>
                    <td className={styles.metricValue}>{metric.value}</td>
                    <td className={metric.change.startsWith('+') ? styles.positive : styles.negative}>{metric.change}</td>
                    <td>{metric.target}</td>
                    <td>{metric.change.startsWith('+') ? <FiCheckCircle className={styles.successIcon} /> : <FiAlertCircle className={styles.warningIcon} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FounderDashboard;